/**
 * The streak card (docs/01 §3.3) — the hero of the chores screen.
 *
 * Three numbers, and the third is the one people actually get wrong: **grace
 * remaining this week**. One grace day per week, and it does not bank — unused
 * grace is gone at the week boundary. If the card only said "streak: 12" nobody
 * would ever know they had a free miss in hand, which is the entire point of
 * the rule.
 *
 * Today's progress is a bar as well as a sentence. A bar is read in one glance
 * where "1 of 3 done today" has to be parsed, and the sentence stays because
 * the bar is decorative to a screen reader.
 *
 * Framing is celebratory throughout. Spent grace is amber, never red: a used
 * grace day means the streak SURVIVED.
 */

import type { StreakState } from '@office/domain'
import type { Tone } from './live.js'
import './phone.css'

export interface StreakCardProps {
  streak: StreakState
  /** How many of the viewer's chores today are done, for the finishing line. */
  doneToday: number
  totalToday: number
}

export function graceLine(graceRemaining: number): string {
  if (graceRemaining <= 0) return 'Grace used this week — the next miss ends the run'
  if (graceRemaining === 1) return '1 grace day left this week'
  return `${graceRemaining} grace days left this week`
}

export function streakLine(currentStreak: number): string {
  if (currentStreak === 0) return 'No streak yet — finish today to start one'
  if (currentStreak === 1) return '1 day streak'
  return `${currentStreak} day streak`
}

function todayLine(doneToday: number, totalToday: number): string {
  if (totalToday === 0) return 'Nothing due today — a rest day never breaks a streak'
  if (doneToday === totalToday) return 'All done today'
  return `${doneToday} of ${totalToday} done today`
}

export function StreakCard({ streak, doneToday, totalToday }: StreakCardProps) {
  const graceTone: Tone = streak.graceRemaining > 0 ? 'good' : 'warn'
  const complete = totalToday > 0 && doneToday === totalToday
  const lit = streak.currentStreak > 0
  const pct = totalToday === 0 ? 0 : Math.round((Math.min(doneToday, totalToday) / totalToday) * 100)

  return (
    <section className="card stack gap-3 ch-streak" aria-labelledby="streak-title">
      <div className="ch-streak-head">
        <h2 id="streak-title" className="ch-streak-label">
          Your streak
        </h2>
      </div>

      <p className="ch-streak-figure">
        <span className="ch-streak-mark" data-lit={lit ? 'true' : 'false'} aria-hidden="true">
          {lit ? '🔥' : '✨'}
        </span>
        {/* The figure is for the eye; `streakLine` already says the number in
            words, and announcing both makes a screen reader read "3, 3 day
            streak". */}
        <strong aria-hidden="true" className="ch-streak-number">
          {streak.currentStreak}
        </strong>
        <span className="ch-streak-caption">{streakLine(streak.currentStreak)}</span>
      </p>

      <p className="ch-streak-chips">
        <span className="ph-chip" data-testid="streak-best">
          Best {streak.longestStreak}
        </span>
        <span className="ph-chip" data-tone={graceTone} data-testid="grace-remaining">
          {graceLine(streak.graceRemaining)}
        </span>
      </p>

      <p className="ch-progress" data-testid="today-progress">
        <span className="ch-progress-track" aria-hidden="true">
          <span
            className="ch-progress-fill"
            data-complete={complete ? 'true' : 'false'}
            style={{ width: `${pct}%` }}
          />
        </span>
        <span className="ch-progress-line">
          {complete ? <span aria-hidden="true">✓ </span> : null}
          {todayLine(doneToday, totalToday)}
        </span>
      </p>

      <p className="ch-streak-note">
        One grace day a week. It never carries over to the next week.
      </p>
    </section>
  )
}
